import { connect } from 'react-redux';
import { round, get } from 'lodash';
import i18next from 'i18next';
import React, { useState, useEffect } from 'react';

import { showConfirmationMessage } from '@actions/messageconfirmation';

import { product as productService } from '@services/product';

import ExportModalContent from '@lib/inpulse/ExportModal';

import utilsXLS from '@commons/utils/makeXLS';

import { getCompositionLabel } from '@admin/products/products/utils/getProductExportLabels';

const BATCH_PRODUCTS_TO_FETCH_AT_ONCE = 50;

export const CentralKitchenProductExport = (props) => {
  const {
    currency,
    params: { title, products, customParams, selectedStatus },
    closeModal,
    showErrorMessage,
  } = props;

  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [titleModal, setTitleModal] = useState(title || '');

  // States for fetching all products by filters
  const [currentPage, setCurrentPage] = useState(1);
  const [detailedProductList, setDetailedProductList] = useState([]);
  const [fetchingPhase, setFetchingPhase] = useState('initial'); // 'initial', 'fetching_ids', 'processing_details', 'completed'

  const COLUMNS_SETTINGS = [
    {
      propertyKey: 'id',
      name: i18next.t('GENERAL.ID'),
    },
    {
      propertyKey: 'name',
      name: i18next.t('GENERAL.NAME'),
    },
    {
      propertyKey: 'mappedStoreName',
      name: i18next.t('GENERAL.CENTRAL'),
    },
    {
      propertyKey: 'status',
      name: i18next.t('GENERAL.STATUS'),
    },
    {
      propertyKey: 'category',
      name: i18next.t('GENERAL.CATEGORY'),
    },
    {
      propertyKey: 'subCategory',
      name: i18next.t('GENERAL.SUB_CATEGORY'),
    },
    {
      propertyKey: 'composition',
      name: i18next.t('ADMIN.RECIPES.EXPORT_COLUMN_PRODUCT_COMPOSITION'),
    },
    {
      type: 'currency',
      propertyKey: 'computedCost',
      name: i18next.t('GENERAL.COST'),
    },
    {
      type: 'currency',
      propertyKey: 'priceHT',
      name: i18next.t('ADMIN.PRODUCTS.EXPORT_COLUMN_PRICE_HT'),
    },
    {
      type: 'percentage',
      propertyKey: 'ratio',
      name: i18next.t('ADMIN.PRODUCTS.EXPORT_COLUMN_RECIPE_RATIO_COST'),
    },
    {
      propertyKey: 'vatRate',
      name: i18next.t('ADMIN.PRODUCTS.EXPORT_COLUMN_VAT_RATE'),
    },
    {
      type: 'currency',
      propertyKey: 'priceWithTaxes',
      name: i18next.t('ADMIN.PRODUCTS.EXPORT_COLUMN_PRICE_INCLUDING_TAXES'),
    },
    {
      propertyKey: 'sku',
      name: i18next.t('GENERAL.SKU'),
    },
    {
      propertyKey: 'shelfLife',
      name: i18next.t('ADMIN.PRODUCTS.EXPORT_COLUMN_SHELF_LIFE'),
    },
    {
      propertyKey: 'portionSizeInGrams',
      name: i18next.t('ADMIN.PRODUCTS.PORTION_SIZE_IN_GRAMS'),
    },
  ];

  const formatDataForXLSFile = (products) => {
    const productsToBeExported = [];

    products.forEach((product) => {
      const productSupplierProductMappings = product.productSupplierProductMappings;

      let priceHT = product.priceWithTaxes ?? null;

      if (product.vatRate > 0) {
        priceHT = Math.round((product.priceWithTaxes / (1 + product.vatRate / 100)) * 100) / 100;
      }

      const mappedEntity = product.entity;

      const computedCost = round(get(mappedEntity, 'cost', 0), 2);
      const ratio = priceHT && computedCost ? round((computedCost / priceHT) * 100, 2) : '';

      productSupplierProductMappings.forEach((pspm) => {
        productsToBeExported.push({
          ...product,
          status: product.active ? i18next.t('GENERAL.ACTIVE') : i18next.t('GENERAL.INACTIVE'),
          priceHT: priceHT || '',
          mappedStoreName: pspm.mappedStoreName,
          composition: getCompositionLabel(product.entity),
          computedCost,
          ratio,
        });
      });
    });

    return productsToBeExported;
  };

  const fetchNextBatch = async () => {
    try {
      const { data, count } = await productService.getPaginatedProductsOfClient(
        {
          orderBy: 'name',
          orderType: 'asc',
          page: currentPage,
          limit: BATCH_PRODUCTS_TO_FETCH_AT_ONCE,
          search: '', // Don't apply search for export
        },
        {
          brandId: customParams.brandId,
          active: { eq: get(selectedStatus, 'filterValue', false) },
          categoryId: customParams.categoryId,
          isIngredient: customParams.isIngredient,
          subCategoryId: customParams.subCategoryId,
          priceWithTaxes: customParams.priceWithTaxes,
          nbCashierProductMappingCount: customParams.nbCashierProductMappingCount,
          isKitchen: { eq: true },
        },
      );

      // Update detailedProductList with new batch
      const updatedProductList = [...detailedProductList, ...data];
      setDetailedProductList(updatedProductList);

      // Update progress for ID fetching phase
      const progressPercentage = Math.min((updatedProductList.length / count) * 100, 100);
      setProgress(progressPercentage);

      // Check if we have more data to fetch
      if (
        data &&
        data.length === BATCH_PRODUCTS_TO_FETCH_AT_ONCE &&
        updatedProductList.length < count
      ) {
        // Continue with next batch
        setCurrentPage(currentPage + 1);
      } else {
        // All products fetched, now start the detailed export process
        setFetchingPhase('processing_details');
        setProgress(100);
      }
    } catch {
      showErrorMessage(i18next.t('GENERAL.EXPORT_FAILURE'));
      exitModal();
    }
  };

  // Initialize: either use provided products or fetch all by filters
  useEffect(() => {
    if (products && products.length) {
      // Export selected products directly
      setDetailedProductList(products);
      setFetchingPhase('processing_details');
    } else if (customParams && selectedStatus) {
      // Start fetching all products by filters
      setFetchingPhase('fetching_ids');
    }
  }, []);

  // Fetch products batch by batch when in fetching_ids phase
  useEffect(() => {
    if (fetchingPhase !== 'fetching_ids') {
      return;
    }

    fetchNextBatch();
  }, [currentPage, fetchingPhase]);

  // Handle the detailed export process
  useEffect(() => {
    if (fetchingPhase === 'processing_details') {
      try {
        const mainSheet = utilsXLS.generateDefaultSheet(
          i18next.t('ADMIN.CENTRAL_PRODUCTS.EXPORT_LIST_MAIN_SHEET_NAME'),
          COLUMNS_SETTINGS,
          formatDataForXLSFile(detailedProductList),
          currency,
        );

        utilsXLS.makeXLS(i18next.t('ADMIN.CENTRAL_PRODUCTS.EXPORT_LIST_FILENAME'), [mainSheet]);

        setTitleModal(i18next.t('GENERAL.EXPORT_SUCCESS'));
        setIsLoading(false);
      } catch {
        showErrorMessage(i18next.t('GENERAL.EXPORT_FAILURE'));
        exitModal();
      }
    }
  }, [fetchingPhase]);

  const exitModal = () => {
    setIsLoading(false);
    closeModal();
  };

  return (
    <ExportModalContent
      {...props}
      closeModal={closeModal}
      exitModal={exitModal}
      isLoading={isLoading}
      progress={progress}
      setLoading={setIsLoading}
      titleModal={titleModal}
    />
  );
};

const mapStateToProps = (state) => ({
  currency: state.baseReducer.currency,
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showConfirmationMessage(message, 'error'));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(CentralKitchenProductExport);
